import { call } from 'redux-saga/effects';
import { getPeer, isDetachedHost } from './peer';

export type RpcRoutingState = {
  mainTab: boolean;
  mainWindowName: string | null;
};

let getTabState: () => RpcRoutingState = () => ({
  mainTab: !isDetachedHost(),
  mainWindowName: window.name,
});

export const configureRpcRouting = (getState: () => RpcRoutingState): void => {
  getTabState = getState;
};

export type RpcRequestOptions = {
  causationId?: string;
  timeoutMs?: number;
};

/**
 * The session every peer of this reading is named after.
 *
 * The form and the viewer keep the identity they were created with, so a
 * detached host addresses `form-<sid>` and `viewer-<sid>` exactly as the main
 * one does. Taken from the routing state rather than `window.name`: the two are
 * the same by construction, but only the state is set deliberately, and reading
 * it here keeps the addressing honest if that ever stops being true.
 */
export const sessionName = (): string =>
  getTabState().mainWindowName ?? window.name;

/**
 * Recognizes a request abandoned because this window is shutting down RPC
 *
 * Plain terms
 * -----------
 * When the reader leaves the page, the connection to the viewer and the form
 * is closed on purpose, and every question still waiting for an answer is
 * dropped. That is the expected end of a session, not a fault, so it must not
 * show up as an error or be reported as one.
 *
 * Technical
 * ---------
 * `rpc-core`'s `peer.destroy()` (called by `disposeRpc` on `pagehide`) rejects
 * every pending request with `{ code: -32001, message: 'Peer destroyed' }`.
 * Callers use this to settle quietly instead of logging / rethrowing.
 */
export const RPC_PEER_DESTROYED_CODE = -32001;

export const isPeerDestroyedError = (error: unknown): boolean =>
  (error as { code?: unknown } | null)?.code === RPC_PEER_DESTROYED_CODE;

/** `rpc-core`'s code for a request nobody answered within its `timeoutMs`. */
export const RPC_TIMEOUT_CODE = -32000;

export const isRpcTimeoutError = (error: unknown): boolean =>
  (error as { code?: unknown } | null)?.code === RPC_TIMEOUT_CODE;

const requestTo = <R = unknown>(
  instance: 'form' | 'viewer',
  method: string,
  payload?: unknown,
  options?: RpcRequestOptions
): Promise<R> =>
  getPeer().request(method, payload, {
    to: `${instance}-${sessionName()}`,
    ...options,
  });

export const requestToViewer = <R = unknown>(
  method: string,
  payload?: unknown,
  options?: RpcRequestOptions
): Promise<R> => requestTo<R>('viewer', method, payload, options);

export const emitToForm = (event: string, payload: unknown): void =>
  getPeer().emit(event, payload, `form-${sessionName()}`);

export const emitToViewer = (event: string, payload: unknown): void =>
  getPeer().emit(event, payload, `viewer-${sessionName()}`);

/**
 * Sends a viewer command and survives an unanswered one.
 *
 * Plain terms
 * -----------
 * These commands are only sent while the viewer is already open — the form
 * asking it to switch an image, or the reader detaching the form into its own
 * window. Still, if the viewer happens to be gone at that moment nobody should
 * lose attach/detach or form sync for the rest of the session: the command is
 * simply reported as unanswered.
 *
 * Technical
 * ---------
 * A saga worker that lets an exception escape kills its parent saga and every
 * watcher it registered — that is how a single `Request timed out` took down
 * `DICOMViewerSaga`. `measurements.saga` guards against this with
 * `keepWatcherAlive`; the tab-coordination and form-management workers had no
 * equivalent.
 */
export function* tellViewer<T>(
  command: (payload: T) => Promise<unknown>,
  payload: T,
  label: string
) {
  try {
    yield call(command, payload);
  } catch (error) {
    if (isPeerDestroyedError(error)) return;

    console.error(`[rpc] the viewer did not acknowledge ${label}`, error);
  }
}

export const requestMain = <R>(
  method: string,
  payload: unknown,
  options?: { mainWindowName?: string | null; timeoutMs?: number }
): Promise<R> => {
  const mainWindowName =
    options?.mainWindowName ?? getTabState().mainWindowName;

  if (!mainWindowName) {
    return Promise.reject(
      new Error(`Cannot call ${method}: main tab is unknown`)
    );
  }

  return getPeer().request<R>(method, payload, {
    to: `d7-${mainWindowName}`,
    ...(options?.timeoutMs !== undefined && { timeoutMs: options.timeoutMs }),
  });
};

export const requestDetached = <R>(
  method: string,
  payload: unknown,
  timeoutMs?: number
): Promise<R> =>
  getPeer().request<R>(method, payload, {
    to: `d7-detached-${window.name}`,
    ...(timeoutMs !== undefined && { timeoutMs }),
  });

/** Main tab → the detached host at its deterministic session address. */
export const emitToDetached = (event: string, payload: unknown): void => {
  const { mainTab } = getTabState();

  if (!mainTab) {
    return;
  }

  getPeer().emit(event, payload, `d7-detached-${window.name}`);
};

/**
 * Detached window → the main tab's host peer.
 *
 * Silent in the main tab itself: `mainWindowName` there is this window's own
 * name, and a peer never receives its own emits, so addressing it would be a
 * no-op that reads like a message. Callers stay free of `mainTab` checks.
 */
export const emitToMain = (event: string, payload: unknown): void => {
  const { mainTab, mainWindowName } = getTabState();

  if (mainTab || !mainWindowName) {
    return;
  }

  getPeer().emit(event, payload, `d7-${mainWindowName}`);
};
